/**
 * Dimension parsing utilities
 * Parse product dimensions from option values (e.g., "30_x_20_cm" → {width: 300, height: 200})
 */

export interface Dimensions {
  width: number;  // in mm
  height: number; // in mm
  unit: 'mm' | 'cm';
  originalValue: string;
}

export interface BleedSettings {
  top: number;    // in mm
  right: number;  // in mm
  bottom: number; // in mm
  left: number;   // in mm
}

export type ProductShape = 'rectangle' | 'circle' | 'hexagon' | 'heart' | 'star' | 'cloud';

export function parseMillimeters(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().replace(',', '.').toLowerCase();
  if (!normalized) return null;

  const match = normalized.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;

  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount < 0) return null;

  if (/\bcm\b/.test(normalized)) return amount * 10;
  return amount;
}

export function getBleedSettingsFromValue(value: unknown): BleedSettings | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const source = value as Record<string, unknown>;
    const top = parseMillimeters(source.top);
    const right = parseMillimeters(source.right);
    const bottom = parseMillimeters(source.bottom);
    const left = parseMillimeters(source.left);
    if ([top, right, bottom, left].every((item) => item !== null)) {
      return { top: top!, right: right!, bottom: bottom!, left: left! };
    }
  }

  const bleed = parseMillimeters(value);
  if (bleed === null) return null;
  return { top: bleed, right: bleed, bottom: bleed, left: bleed };
}

function isPendantContext(value: string): boolean {
  return (
    value.includes('foto-hanger') ||
    value.includes('fotohanger') ||
    value.includes('photo pendant') ||
    value.includes('pendant') ||
    value.includes('hanger')
  );
}

export function getProductShape(sizeValue?: string | null): ProductShape {
  const value = String(sizeValue || '').toLowerCase();
  // "shape:rounded_corners" bevat toevallig de deelstring "round" ("rounded"),
  // en zonder deze uitzondering matchte de cirkel-check hieronder als eerste.
  // Daardoor kreeg de "Afgeronde hoeken"-vormpreset een ronde hulplijn in de
  // editor: een vierkant product met vier scherpe hoeken zag een cirkel die
  // ze alle vier wegsneed. Rechthoekige hoeken (afgerond of niet) horen bij
  // 'rectangle', niet bij 'circle'.
  if (value.includes('shape:rounded_corners') || value.includes('shape:rounded-corners')) {
    return 'rectangle';
  }
  if (
    value.includes('shape:circle') ||
    value.includes('circle') ||
    value.includes('circular') ||
    value.includes('round') ||
    value.includes('rond') ||
    value.includes('cirkel') ||
    value.includes('diameter') ||
    value.includes('ø') ||
    value.includes('⌀')
  ) return 'circle';
  if (value.includes('shape:hexagon') || value.includes('hexagon') || value.includes('zeshoek')) return 'hexagon';
  if (value.includes('shape:heart') || value.includes('heart') || value.includes('hart')) return 'heart';
  if (value.includes('shape:star') || value.includes('star') || value.includes('ster')) return 'star';
  if (value.includes('shape:cloud') || value.includes('cloud') || value.includes('wolk')) return 'cloud';
  return 'rectangle';
}

/**
 * Herkent een vorm alleen via onze eigen expliciete "|shape:x"-notatie (zie
 * getConfiguredShapeSuffix in product-configurator.tsx), niet via losse
 * trefwoorden in de maat zelf.
 *
 * getProductShape() hierboven matcht ook op losse woorden als "circle" of
 * "rond" in de ruwe Print.com-maatsleutel, nodig voor de hulplijn op
 * producten die van zichzelf al een vaste vorm hebben (zoals wandcirkels met
 * maatsleutel "circle_60_cm", zonder |shape:-suffix). Voor het genereren van
 * een snijcontour is dat te los: een geraden vorm op een product dat
 * Print.com vandaag al correct als rechthoek snijdt, is een nieuw defect op
 * iets dat nu werkt. Deze functie geeft daarom alleen een vorm terug als de
 * eigen preset-syntax letterlijk aanwezig is, en anders null.
 */
export function getExplicitShapePreset(sizeValue?: string | null): ProductShape | null {
  const value = String(sizeValue || '').toLowerCase();
  if (/\|shape:rounded[_-]corners\b/.test(value)) return 'rectangle';
  if (/\|shape:(circle|round)\b/.test(value)) return 'circle';
  if (/\|shape:hexagon\b/.test(value)) return 'hexagon';
  if (/\|shape:heart\b/.test(value)) return 'heart';
  if (/\|shape:star\b/.test(value)) return 'star';
  if (/\|shape:cloud\b/.test(value)) return 'cloud';
  return null;
}

/**
 * Parse dimensions from size string
 * Supports formats: "30_x_20_cm", "300x200", "A4", etc.
 */
export function parseDimensions(sizeValue: string): Dimensions | null {
  if (!sizeValue) return null;

  const normalized = sizeValue
    .trim()
    .replace(/[″”]/g, '"')
    .replace(/×/g, 'x');

  // Print.com levert ronde maten als sleutel met liggende streepjes:
  // "circle_30_cm". Die scheiding stond niet in het patroon, dus viel de hele
  // functie terug op null en had de ontwerpmodule voor een behangcirkel geen
  // afmeting: geen snijlijnen, geen dpi-controle en geen printformaat. De
  // scheiding mag nu ook een liggend streepje zijn, voor en na het getal.
  const diameterMatch = normalized.match(/(?:diameter|circular|circle|round|rond|cirkel|[⌀Øø])[\s_:-]*(\d+(?:[.,]\d+)?)[\s_-]*(mm|cm|in|inch|inches|")?/i);
  if (diameterMatch) {
    const [, diameterStr, unit = ''] = diameterMatch;
    const parsed = parseFloat(diameterStr.replace(',', '.'));
    const normalizedUnit = unit.toLowerCase() || (/\b(in|inch|inches)\b|"/i.test(normalized) ? 'in' : 'cm');
    const multiplier = normalizedUnit === 'cm'
      ? 10
      : ['in', 'inch', 'inches', '"'].includes(normalizedUnit)
        ? 25.4
        : 1;
    const diameter = Math.round(parsed * multiplier);
    if (Number.isFinite(diameter) && diameter > 0) {
      return {
        width: diameter,
        height: diameter,
        unit: normalizedUnit === 'cm' ? 'cm' : 'mm',
        originalValue: sizeValue,
      };
    }
  }

  const paperSizes: Record<string, { width: number; height: number }> = {
    A0: { width: 841, height: 1189 },
    A1: { width: 594, height: 841 },
    A2: { width: 420, height: 594 },
    A3: { width: 297, height: 420 },
    A4: { width: 210, height: 297 },
    A5: { width: 148, height: 210 },
    A6: { width: 105, height: 148 },
    B1: { width: 707, height: 1000 },
  };

  const paperMatch = normalized.match(/\b(A[0-6]|B1)\b/i);
  if (paperMatch) {
    const key = paperMatch[1].toUpperCase();
    return {
      ...paperSizes[key],
      unit: 'mm',
      originalValue: sizeValue,
    };
  }

  // Format: "30_x_20_cm" or "300_x_200_mm"
  const underscoreMatch = normalized.match(/(\d+(?:\.\d+)?)_x_(\d+(?:\.\d+)?)_(cm|mm)/i);
  if (underscoreMatch) {
    const [, widthStr, heightStr, unit] = underscoreMatch;
    const width = parseFloat(widthStr);
    const height = parseFloat(heightStr);

    return {
      width: Math.round(unit === 'cm' ? width * 10 : width),
      height: Math.round(unit === 'cm' ? height * 10 : height),
      unit: unit as 'mm' | 'cm',
      originalValue: sizeValue,
    };
  }

  // Format: "300x200" (assume mm)
  const simpleMatch = normalized.match(/(\d+(?:[.,]\d+)?)\s*(in|inch|inches|")?\s*(?:x|X|×|_)\s*(\d+(?:[.,]\d+)?)(?:\s|-)*(cm|mm|in|inch|inches|")?/i);
  if (simpleMatch) {
    const [, widthStr, widthUnit = '', heightStr, heightUnit = ''] = simpleMatch;
    const unit = heightUnit || widthUnit || 'mm';
    const normalizedUnit = unit.toLowerCase();
    const width = parseFloat(widthStr.replace(',', '.'));
    const height = parseFloat(heightStr.replace(',', '.'));
    const multiplier = normalizedUnit === 'cm'
      ? 10
      : ['in', 'inch', 'inches', '"'].includes(normalizedUnit)
        ? 25.4
        : 1;
    return {
      width: Math.round(width * multiplier),
      height: Math.round(height * multiplier),
      unit: normalizedUnit === 'cm' ? 'cm' : 'mm',
      originalValue: sizeValue,
    };
  }

  // Format: "30 x 20 cm"
  const spacedMatch = sizeValue.match(/(\d+)\s*x\s*(\d+)\s*(cm|mm)/i);
  if (spacedMatch) {
    const [, widthStr, heightStr, unit] = spacedMatch;
    const width = parseInt(widthStr);
    const height = parseInt(heightStr);

    return {
      width: unit === 'cm' ? width * 10 : width,
      height: unit === 'cm' ? height * 10 : height,
      unit: unit as 'mm' | 'cm',
      originalValue: sizeValue,
    };
  }

  return null;
}

export function formatMetricSizeLabel(label?: string | number | null): string {
  const text = String(label || '').trim();
  if (!text) return '';

  const dimensions = parseDimensions(text);
  if (!dimensions) return text;

  const hasExplicitMetricUnit = /\b(cm|mm)\b/i.test(text);
  const looksLikeInches = /\b(in|inch|inches)\b|["″”]/i.test(text);
  if (!looksLikeInches && hasExplicitMetricUnit) return text;

  const widthCm = dimensions.width / 10;
  const heightCm = dimensions.height / 10;
  const formatCm = (value: number) => (
    Number.isInteger(value)
      ? String(value)
      : String(Number(value.toFixed(1))).replace('.', ',')
  );

  return `${formatCm(widthCm)} x ${formatCm(heightCm)} cm`;
}

/**
 * Get bleed settings for a product category
 */
export function getBleedSettings(category?: string): BleedSettings {
  const categoryLower = category?.toLowerCase() || '';

  if (isPendantContext(categoryLower)) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };
  }

  if (categoryLower.includes('tijdelijke-tattoo') || categoryLower.includes('temporary tattoo')) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };
  }

  if (
    categoryLower.includes('textielposter') ||
    categoryLower.includes('textile-poster') ||
    categoryLower.includes('wandkleed') ||
    categoryLower.includes('wallcloth') ||
    categoryLower.includes('wall-hanging') ||
    categoryLower.includes('lichtbak') ||
    categoryLower.includes('illuminated-sign') ||
    categoryLower.includes('sierkussen') ||
    categoryLower.includes('decorative-cushion') ||
    categoryLower.includes('dekokissen')
  ) {
    return {
      top: 10,
      right: 10,
      bottom: 10,
      left: 10,
    };
  }

  // Canvas has larger bleed for wrapping around frame
  if (categoryLower.includes('canvas')) {
    return {
      top: 48,
      right: 48,
      bottom: 48,
      left: 48,
    };
  }

  // Default: 3mm bleed for most print products
  return {
    top: 3,
    right: 3,
    bottom: 3,
    left: 3,
  };
}

/**
 * Get safety margin (keep important content within this area)
 */
export function getSafetyMargin(category?: string): number {
  const categoryLower = category?.toLowerCase() || '';

  if (isPendantContext(categoryLower)) {
    return 1;
  }

  if (categoryLower.includes('tijdelijke-tattoo') || categoryLower.includes('temporary tattoo')) {
    return 2;
  }

  if (
    categoryLower.includes('textielposter') ||
    categoryLower.includes('textile-poster') ||
    categoryLower.includes('wandkleed') ||
    categoryLower.includes('wallcloth') ||
    categoryLower.includes('wall-hanging') ||
    categoryLower.includes('lichtbak') ||
    categoryLower.includes('illuminated-sign') ||
    categoryLower.includes('sierkussen') ||
    categoryLower.includes('decorative-cushion') ||
    categoryLower.includes('dekokissen')
  ) {
    return 30;
  }

  // Canvas has no safety margin (image goes to edge)
  if (categoryLower.includes('canvas')) {
    return 0;
  }

  // Default: 3mm safety margin from trim line
  return 3;
}

/**
 * Calculate total dimensions including bleed
 */
export function getDimensionsWithBleed(
  dimensions: Dimensions,
  bleed: BleedSettings
): Dimensions {
  return {
    width: dimensions.width + bleed.left + bleed.right,
    height: dimensions.height + bleed.top + bleed.bottom,
    unit: dimensions.unit,
    originalValue: dimensions.originalValue,
  };
}

/**
 * Format dimensions as display string
 */
export function formatDimensions(dimensions: Dimensions): string {
  if (dimensions.unit === 'cm') {
    return `${dimensions.width / 10} x ${dimensions.height / 10} cm`;
  }
  return `${dimensions.width} x ${dimensions.height} mm`;
}
